import * as THREE from 'three';

/**
 * Finite labeled grid — modernized for Three.js 0.170+
 * Follows the original LabeledGrid pattern: center-based grid with
 * main grid, sub grid, double-border margin, and edge labels.
 * Grid is drawn on XY plane, then oriented via upVector (default Y-up → XZ ground plane).
 *
 * Grid lines (mm + cm) are rendered as a single tiled texture on a plane.
 * Mipmaps provide natural LOD: mm lines fade smoothly at distance (no moiré).
 * Axis lines, ruler ticks, labels, and margin are separate geometry.
 */
export default class LabeledGrid extends THREE.Object3D {
  private _width: number;
  private _length: number;
  private _step: number;
  private _color: number;
  private _opacity: number;
  private _text: boolean;
  private _textColor: string;
  private _textLocation: string;
  private _upVector: THREE.Vector3;
  private _cmBorderColor: string;
  private _labelStrokeColor: string;

  private _gridPlane!: THREE.Mesh;
  private _gridTexture!: THREE.CanvasTexture;
  private mainGrid!: THREE.Object3D; // container for labels
  private margin!: THREE.LineSegments;
  private labels!: THREE.Object3D;

  private marginSize: number;
  private stepSubDivisions: number;

  // LOD: references to mm tick materials that fade with camera distance
  private _mmTickMats: THREE.LineBasicMaterial[] = [];
  private _mmTickBaseOpacities: number[] = [];

  constructor(
    width = 200,
    length = 200,
    step = 100,
    upVector: number[] = [0, 1, 0],
    color = 0x00baff,
    opacity = 0.2,
    text = true,
    textColor = '#000000',
    textLocation = 'center',
    cmBorderColor = 'rgba(58,74,94,',
    labelStrokeColor = 'rgba(255,255,255,0.7)',
  ) {
    super();

    this._width = width;
    this._length = length;
    this._step = step;
    this._color = color;
    this._opacity = opacity;
    this._text = text;
    this._textColor = textColor;
    this._textLocation = textLocation;
    this._upVector = new THREE.Vector3().fromArray(upVector);
    this._cmBorderColor = cmBorderColor;
    this._labelStrokeColor = labelStrokeColor;

    this.name = 'grid';
    this.marginSize = Math.max(0.5, Math.min(width, length) * 0.03);
    this.stepSubDivisions = 10; // 10 subdivisions per step → mm divisions for 1cm step

    this._drawGrid();

    // Grid is drawn on XY plane; rotate -90° around X to lay it on XZ (Y-up)
    if (this._upVector.y === 1) {
      this.rotation.set(-Math.PI / 2, 0, 0);
    } else {
      this.up = this._upVector;
      this.lookAt(this._upVector);
    }
  }

  _drawGrid(): void {
    const mainZ = -0.05;
    const subZ = -0.05;

    const step = this._step;
    const hw = this._width / 2;
    const hl = this._length / 2;

    this._mmTickMats = [];
    this._mmTickBaseOpacities = [];

    // --- Grid texture plane (mm + cm lines via tiled texture) ---
    this._gridTexture = this._makeGridTexture();
    const planeGeo = new THREE.PlaneGeometry(this._width, this._length);
    planeGeo.translate(0, 0, mainZ);
    const planeMat = new THREE.MeshBasicMaterial({
      map: this._gridTexture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this._gridPlane = new THREE.Mesh(planeGeo, planeMat);
    this.add(this._gridPlane);

    // mainGrid container (holds labels)
    this.mainGrid = new THREE.Object3D();
    this.add(this.mainGrid);

    // --- Colored axis lines ---
    const axisZ = mainZ + 0.001; // slightly above grid to prevent z-fighting
    const xAxisGeo = new THREE.BufferGeometry();
    xAxisGeo.setAttribute('position', new THREE.Float32BufferAttribute(
      [-hl, 0, axisZ, hl, 0, axisZ], 3,
    ));
    this.add(new THREE.LineSegments(xAxisGeo, new THREE.LineBasicMaterial({ color: 0xff4444 })));

    const yAxisGeo = new THREE.BufferGeometry();
    yAxisGeo.setAttribute('position', new THREE.Float32BufferAttribute(
      [0, -hw, axisZ, 0, hw, axisZ], 3,
    ));
    this.add(new THREE.LineSegments(yAxisGeo, new THREE.LineBasicMaterial({ color: 0x44cc44 })));

    // --- Ruler-style tick marks (one side only) ---
    const subStep = step / this.stepSubDivisions;
    const cmTickLen = this._step * 0.1;
    const halfCmTickLen = cmTickLen * 0.65;
    const mmTickLen = cmTickLen * 0.7;
    const halfStep = step / 2;

    const xCmPts: number[] = [];
    const yCmPts: number[] = [];
    const xMmPts: number[] = [];
    const yMmPts: number[] = [];

    // X-axis ticks (perpendicular in -Y direction)
    for (let i = subStep; i <= hl; i += subStep) {
      const iRound = Math.round(i * 1000) / 1000;
      const onCm = Math.abs(iRound % step) < 0.001;
      const onHalfCm = !onCm && Math.abs(iRound % halfStep) < 0.001;
      const len = onCm ? cmTickLen : onHalfCm ? halfCmTickLen : mmTickLen;
      const pts = onCm ? xCmPts : xMmPts;
      pts.push(i, 0, mainZ, i, -len, mainZ);
      pts.push(-i, 0, mainZ, -i, -len, mainZ);
    }

    // Y-axis ticks (perpendicular in -X direction)
    for (let i = subStep; i <= hw; i += subStep) {
      const iRound = Math.round(i * 1000) / 1000;
      const onCm = Math.abs(iRound % step) < 0.001;
      const onHalfCm = !onCm && Math.abs(iRound % halfStep) < 0.001;
      const len = onCm ? cmTickLen : onHalfCm ? halfCmTickLen : mmTickLen;
      const pts = onCm ? yCmPts : yMmPts;
      pts.push(0, i, mainZ, -len, i, mainZ);
      pts.push(0, -i, mainZ, -len, -i, mainZ);
    }

    // CM ticks (color-coded, full opacity)
    if (xCmPts.length > 0) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(xCmPts, 3));
      this.add(new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0xff4444 })));
    }
    if (yCmPts.length > 0) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(yCmPts, 3));
      this.add(new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ color: 0x44cc44 })));
    }
    // MM + half-cm ticks (color-coded, slightly transparent)
    if (xMmPts.length > 0) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(xMmPts, 3));
      const mat = new THREE.LineBasicMaterial({
        color: 0xff4444, opacity: 0.6, transparent: true,
      });
      this.add(new THREE.LineSegments(geo, mat));
      this._mmTickMats.push(mat);
      this._mmTickBaseOpacities.push(0.6);
    }
    if (yMmPts.length > 0) {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(yMmPts, 3));
      const mat = new THREE.LineBasicMaterial({
        color: 0x44cc44, opacity: 0.6, transparent: true,
      });
      this.add(new THREE.LineSegments(geo, mat));
      this._mmTickMats.push(mat);
      this._mmTickBaseOpacities.push(0.6);
    }

    // --- Margin (double border: inner at grid edge, outer offset by marginSize) ---
    const marginPts: number[] = [];
    const ohw = (this._width + this.marginSize) / 2;
    const ohl = (this._length + this.marginSize) / 2;

    // Inner border
    marginPts.push(-hl, -hw, subZ, hl, -hw, subZ);
    marginPts.push(hl, -hw, subZ, hl, hw, subZ);
    marginPts.push(hl, hw, subZ, -hl, hw, subZ);
    marginPts.push(-hl, hw, subZ, -hl, -hw, subZ);

    // Outer border
    marginPts.push(-ohl, -ohw, subZ, ohl, -ohw, subZ);
    marginPts.push(ohl, -ohw, subZ, ohl, ohw, subZ);
    marginPts.push(ohl, ohw, subZ, -ohl, ohw, subZ);
    marginPts.push(-ohl, ohw, subZ, -ohl, -ohw, subZ);

    const marginGeo = new THREE.BufferGeometry();
    marginGeo.setAttribute('position', new THREE.Float32BufferAttribute(marginPts, 3));
    const marginMat = new THREE.LineBasicMaterial({
      color: this._color,
      opacity: this._opacity * 2,
      transparent: true,
    });
    this.margin = new THREE.LineSegments(marginGeo, marginMat);
    this.add(this.margin);

    this._drawNumbering();
  }

  // --- Public API ---

  toggle(visible: boolean): void {
    this.traverse((child) => {
      child.visible = visible;
    });
  }

  setOpacity(opacity: number): void {
    this._opacity = opacity;
    (this._gridPlane.material as THREE.MeshBasicMaterial).opacity = opacity;
    (this.margin.material as THREE.LineBasicMaterial).opacity = opacity * 2;
  }

  setColor(color: number): void {
    this._color = color;
    // Regenerate texture with new color
    this._gridTexture.dispose();
    this._gridTexture = this._makeGridTexture();
    (this._gridPlane.material as THREE.MeshBasicMaterial).map = this._gridTexture;
    (this._gridPlane.material as THREE.MeshBasicMaterial).needsUpdate = true;
    (this.margin.material as THREE.LineBasicMaterial).color = new THREE.Color().setHex(color);
  }

  toggleText(visible: boolean): void {
    this._text = visible;
    this.labels.traverse((child) => {
      child.visible = visible;
    });
  }

  setTextColor(color: string): void {
    this._textColor = color;
    this._drawNumbering();
  }

  setTextLocation(location: string): void {
    this._textLocation = location;
    this._drawNumbering();
  }

  setUp(upVector: THREE.Vector3): void {
    this._upVector = upVector;
    this.up = upVector;
    this.lookAt(upVector);
  }

  resize(width: number, length: number): void {
    if (width && length) {
      this._width = Math.max(width, 10);
      this._length = Math.max(length, 10);
      this._step = Math.max(this._step, 5);

      this._disposeChildren();
      this._drawGrid();
    }
  }

  /** Update LOD fading for mm ruler ticks based on camera distance.
   *  Grid texture LOD is handled automatically by GPU mipmaps. */
  updateLOD(cameraDistance: number): void {
    const step = this._step;
    const fadeOutDist = step * 80;
    const fadeInDist = step * 30;

    let fade: number;
    if (cameraDistance >= fadeOutDist) {
      fade = 0;
    } else if (cameraDistance <= fadeInDist) {
      fade = 1;
    } else {
      fade = 1 - (cameraDistance - fadeInDist) / (fadeOutDist - fadeInDist);
    }

    // Fade mm tick marks
    for (let i = 0; i < this._mmTickMats.length; i++) {
      this._mmTickMats[i].opacity = this._mmTickBaseOpacities[i] * fade;
    }
  }

  /** No-op — kept for API compatibility. Texture-based grid doesn't need resolution. */
  setResolution(_width: number, _height: number): void {
    // Grid texture uses mipmaps; no per-frame resolution update needed.
  }

  dispose(): void {
    this._disposeChildren();
  }

  // --- Private ---

  /** Create a tiled canvas texture for one step×step grid cell.
   *  Draws mm sub-grid lines and cm border lines.
   *  Mipmaps handle LOD: mm lines blur/fade at distance. */
  private _makeGridTexture(): THREE.CanvasTexture {
    const tilePixels = 200; // pixels per step cell (20px per mm)
    const subs = this.stepSubDivisions;
    const subPx = tilePixels / subs;

    const canvas = document.createElement('canvas');
    canvas.width = tilePixels;
    canvas.height = tilePixels;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, tilePixels, tilePixels);

    const gridColor = new THREE.Color(this._color);
    const r = Math.round(gridColor.r * 255);
    const g = Math.round(gridColor.g * 255);
    const b = Math.round(gridColor.b * 255);

    // mm sub-grid lines (thin, low opacity)
    const mmAlpha = Math.min(1, this._opacity * 0.3);
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${mmAlpha})`;
    for (let i = 1; i < subs; i++) {
      const pos = Math.round(i * subPx);
      ctx.fillRect(pos, 0, 1, tilePixels); // vertical
      ctx.fillRect(0, pos, tilePixels, 1); // horizontal
    }

    // cm border lines at tile edges (seamless when tiled via RepeatWrapping)
    // Draw 2px on each side of tile boundary → 4px cm line when tiled
    const cmAlpha = Math.min(1, this._opacity * 0.9);
    ctx.fillStyle = `${this._cmBorderColor}${cmAlpha})`;
    // Vertical cm lines
    ctx.fillRect(0, 0, 2, tilePixels);
    ctx.fillRect(tilePixels - 2, 0, 2, tilePixels);
    // Horizontal cm lines
    ctx.fillRect(0, 0, tilePixels, 2);
    ctx.fillRect(0, tilePixels - 2, tilePixels, 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.anisotropy = 16;
    texture.generateMipmaps = true;
    texture.repeat.set(this._width / this._step, this._length / this._step);

    return texture;
  }

  /** Dispose and remove all child objects */
  private _disposeChildren(): void {
    const disposeMesh = (obj: THREE.Object3D) => {
      if (obj instanceof THREE.LineSegments || obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const mat = obj.material;
        if (Array.isArray(mat)) {
          mat.forEach((m) => m.dispose());
        } else {
          if ((mat as THREE.MeshBasicMaterial).map) {
            (mat as THREE.MeshBasicMaterial).map!.dispose();
          }
          mat.dispose();
        }
      }
    };
    this.traverse(disposeMesh);
    this.clear();
    this._mmTickMats = [];
    this._mmTickBaseOpacities = [];
  }

  private _drawNumbering(): void {
    const step = this._step;
    const subStep = step / this.stepSubDivisions;
    const hw = this._width / 2;
    const hl = this._length / 2;

    // Remove old labels
    if (this.labels) {
      this.labels.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          const mat = obj.material as THREE.MeshBasicMaterial;
          if (mat.map) mat.map.dispose();
          mat.dispose();
        }
      });
      this.mainGrid.remove(this.labels);
    }
    this.labels = new THREE.Object3D();

    const labelZ = -0.05; // same Z as grid lines (mainZ)
    const labelH = this._step * 0.12;
    const labelW = labelH * 4;
    // Perpendicular offset from axis
    const labelPerp = labelH / 2 + this._step * 0.01;

    const borderGap = this._step * 0.001;

    // Labels along X axis — left edge at grid line, below axis
    for (let i = -hl; i <= hl; i += step) {
      if (i === 0) continue;
      const label = this._makeLabel(String(i));
      label.position.set(i + labelW / 2, -labelPerp, labelZ);
      this.labels.add(label);
    }

    // Labels along Y axis — like X: right of axis, top edge at grid line
    for (let i = -hw; i <= hw; i += step) {
      if (i === 0) continue;
      const label = this._makeLabel(String(i));
      label.position.set(labelW / 2 + labelPerp, i - labelH / 2, labelZ);
      this.labels.add(label);
    }

    // Border labels — X values along bottom edge (just inside border)
    for (let i = -hl; i <= hl; i += step) {
      if (i === 0) continue;
      const label = this._makeLabel(String(i));
      label.position.set(i + labelW / 2, -hw + labelH / 2, labelZ);
      this.labels.add(label);
    }

    // Border labels — X values along top edge (just inside border)
    for (let i = -hl; i <= hl; i += step) {
      if (i === 0) continue;
      const label = this._makeLabel(String(i));
      label.position.set(i + labelW / 2, hw - labelH / 2, labelZ);
      this.labels.add(label);
    }

    // Border labels — Y values along left edge (skip corners to avoid duplicates)
    for (let i = -hw; i <= hw; i += step) {
      if (i === 0 || Math.abs(i) === hw) continue;
      const label = this._makeLabel(String(i));
      label.position.set(-hl + labelW / 2, i - labelH / 2, labelZ);
      this.labels.add(label);
    }

    // Border labels — Y values along right edge (skip corners to avoid duplicates)
    for (let i = -hw; i <= hw; i += step) {
      if (i === 0 || Math.abs(i) === hw) continue;
      const label = this._makeLabel(String(i), 'right');
      label.position.set(hl - labelW / 2, i - labelH / 2, labelZ);
      this.labels.add(label);
    }

    // Apply text visibility
    const textVisible = this._text;
    this.labels.traverse((child) => {
      child.visible = textVisible;
    });

    this.mainGrid.add(this.labels);
  }

  private _makeLabel(text: string, align: 'left' | 'right' = 'left'): THREE.Mesh {
    const cw = 256;
    const ch = 64;
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d')!;
    ctx.font = 'bold 48px monospace';
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';
    const tx = align === 'left' ? 4 : cw - 4;
    // Outline for contrast
    ctx.strokeStyle = this._labelStrokeColor;
    ctx.lineWidth = 3;
    ctx.strokeText(text, tx, ch / 2);
    ctx.fillStyle = this._textColor;
    ctx.fillText(text, tx, ch / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearFilter;

    const mat = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      color: 0xffffff,
      alphaTest: 0.1,
      side: THREE.DoubleSide,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    // Wide-short plane matching canvas aspect ratio (4:1)
    const h = this._step * 0.12;
    const w = h * 4;
    return new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
  }
}
